import React, { useState, useEffect } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
const API = process.env.REACT_APP_API_URL || "https://api.pixlybooth.com";

function BackdropCard({ item, size = "md" }) {
  const height = size === "lg" ? "h-44 md:h-52" : "h-36 md:h-40";

  return (
    <div
      className="group relative overflow-hidden rounded-2xl border border-white/10 bg-white/[0.04]
                 shadow-[0_14px_30px_rgba(0,0,0,0.45)]
                 hover:shadow-[0_18px_45px_rgba(227,67,100,0.20)]
                 hover:-translate-y-1 transition-all duration-300"
    >
      <div className={`relative ${height}`}>
        {item.image_url ? (
          <img
            src={item.image_url}
            alt={item.name}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            loading="lazy"
          />
        ) : (
          <div className="w-full h-full bg-white/5 flex items-center justify-center">
            <span className="text-4xl opacity-30">🖼️</span>
          </div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(227,67,100,0.18),transparent_55%)]" />
      </div>

      <div className="absolute bottom-3 left-3 right-3 flex items-center justify-between">
        <span className="text-[11px] md:text-xs font-semibold tracking-[0.28em] uppercase text-white">
          {item.name}
        </span>
        {parseFloat(item.price_addon || 0) > 0 && (
          <span className="text-[10px] text-white/70 bg-black/35 border border-white/10 px-2 py-1 rounded-full backdrop-blur-md">
            +${parseFloat(item.price_addon).toFixed(0)}
          </span>
        )}
      </div>
    </div>
  );
}

function SkeletonCard({ size = "md" }) {
  const height = size === "lg" ? "h-44 md:h-52" : "h-36 md:h-40";
  return (
    <div className={`rounded-2xl border border-white/10 bg-white/[0.04] ${height} animate-pulse`} />
  );
}

export default function BackdropGrid() {
  const [backdrops, setBackdrops] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios
      .get(`${API}/api/backdrops`)
      .then((r) => setBackdrops(r.data.backdrops || []))
      .catch(() => setBackdrops([]))
      .finally(() => setLoading(false));
  }, []);

  // Split: first 3 are "featured" (large), rest are standard
  const featured = backdrops.slice(0, 3);
  const rest = backdrops.slice(3);

  return (
    <section id="backdrop-gallery" className="py-16">
      <div className="max-w-6xl mx-auto px-4 md:px-8">
        <div className="text-center mb-10 animate-fade-up">
          <p className="text-xs uppercase tracking-[0.3em] text-[#E34364] mb-2">Backdrops</p>
          <h2 className="text-3xl md:text-4xl font-bold mb-3">Choose Your Backdrop</h2>
          <p className="text-gray-300 max-w-2xl mx-auto text-sm md:text-base">
            From shimmering sequins to elegant florals and lush hedge walls —
            pick the perfect backdrop to match your vibe.
          </p>
        </div>

        {loading ? (
          <>
            <div className="grid md:grid-cols-3 gap-5 mb-5">
              {[0, 1, 2].map((i) => <SkeletonCard key={i} size="lg" />)}
            </div>
            <div className="grid md:grid-cols-3 gap-5">
              {[0, 1, 2].map((i) => <SkeletonCard key={i} />)}
            </div>
          </>
        ) : backdrops.length === 0 ? (
          <div className="text-center py-20 text-gray-500">
            <span className="text-5xl block mb-4">🖼️</span>
            <p>No backdrops available yet. Check back soon!</p>
          </div>
        ) : (
          <>
            {/* Featured row — first 3 as large cards */}
            {featured.length > 0 && (
              <div className="grid md:grid-cols-3 gap-5 mb-5 animate-fade-up">
                {featured.map((b) => (
                  <BackdropCard key={b.id} item={b} size="lg" />
                ))}
              </div>
            )}

            {/* Rest of backdrops */}
            {rest.length > 0 && (
              <div className="grid grid-cols-2 md:grid-cols-3 gap-5 mb-5 animate-fade-up">
                {rest.map((b) => (
                  <BackdropCard key={b.id} item={b} />
                ))}
              </div>
            )}
          </>
        )}

        {/* Custom / CTA card — always shown at bottom */}
        <div className="mt-5 rounded-2xl border border-white/10 bg-white/[0.04] p-6
                        shadow-[0_14px_30px_rgba(0,0,0,0.45)] flex flex-col md:flex-row md:items-center justify-between gap-6 animate-fade-up">
          <div>
            <p className="text-xs uppercase tracking-[0.3em] text-[#E34364] mb-2">Need Custom?</p>
            <h3 className="text-lg font-semibold mb-2">Brand &amp; Theme Matching</h3>
            <p className="text-sm text-gray-300 max-w-md">
              Want a specific color, logo, or theme? We can arrange custom setups for corporate
              activations and weddings.
            </p>
          </div>
          <Link to="/book" className="shrink-0 inline-flex justify-center items-center rounded-full bg-[#E34364]
                       px-6 py-3 text-sm font-semibold shadow-[0_18px_45px_rgba(227,67,100,0.45)]
                       hover:bg-[#f15477] transition whitespace-nowrap"
          >
            Request a Quote
          </Link>
        </div>
      </div>
    </section>
  );
}
